import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import i18n from "../i18n";

import { newId } from "../utils/ids";
import { getInitialMessages, getInitialCTAs } from "./chat.constants";
import {
  buildMessagesWithTyping,
  buildCartItems,
  buildCartSummary,
} from "./chat.selectors";

import { syncCartFromServer } from "./session/session.cart";
import { callChatApi, loadChatSession } from "./session/session.chat";
import { dispatchSessionAction } from "./session/session.actions";
import { saveChatMessage } from "../api/chatApi";

export function useChatSession() {
  const [chatId, setChatId] = useState(() => newId());
  const [screen, setScreen] = useState("chat");

  const [messages, setMessages] = useState(() => getInitialMessages());
  const [ctas, setCtas] = useState(() => getInitialCTAs());

  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(false);

  const reqSeqRef = useRef(0);
  const productIndexRef = useRef({});

  const [preset, setPreset] = useState("dine_in");

  const messagesWithTyping = useMemo(
    () => buildMessagesWithTyping(messages, loading),
    [messages, loading]
  );

  const cartItems = useMemo(() => buildCartItems(cart), [cart]);
  const cartSummary = useMemo(() => buildCartSummary(cartItems), [cartItems]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCart({});
        return;
      }

      try {
        await syncCartFromServer({ setCart });
      } catch (e) {
        console.error("syncCartFromServer error:", e);
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    function handleLanguageChanged() {
      setMessages((prev) => {
        const isWelcomeOnly =
          prev.length === 1 &&
          prev[0]?.role === "assistant" &&
          !prev[0]?.text &&
          Array.isArray(prev[0]?.parts);

        return isWelcomeOnly ? getInitialMessages() : prev;
      });

      setCtas(getInitialCTAs());
    }

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  async function callChat(text, historyOverride) {
    await callChatApi({
      text,
      historyOverride,
      chatId,
      cart,
      reqSeqRef,
      setLoading,
      setMessages,
      setCtas,
      setScreen,
      setCart,
      productIndexRef,
    });
  }

  async function send(text) {
    const t = String(text || "").trim();
    if (!t) return;

    const isWelcomeOnly =
      messages.length === 1 &&
      messages[0]?.role === "assistant" &&
      !messages[0]?.text &&
      Array.isArray(messages[0]?.parts);

    const baseHistory = isWelcomeOnly ? [] : messages;
    const nextHistory = [...baseHistory, { role: "user", text: t }];

    setMessages(nextHistory);

    await callChat(t, nextHistory);
  }

  async function appendOrderConfirmation(order, forcedChatId = null) {
  if (!order) return;

  const orderId = String(
    order.id || order.receipt_number || order.number || Date.now()
  );

  const isUk = (i18n.resolvedLanguage || i18n.language || "uk")
    .toLowerCase()
    .startsWith("uk");

  const text = isUk
    ? `Привіт!
Дякуємо за ваше замовлення — ми успішно його отримали.

Якщо у вас виникнуть питання — просто напишіть нам, ми з радістю допоможемо.

Дякуємо, що обрали нас!`
    : `Hi there!
Thank you for your order — we’ve received it successfully.

If you have any questions, just reply here — we’re happy to help.

Thanks again for shopping with us!`;

  const message = {
    id: `order-confirmation-${orderId}`,
    role: "assistant",
    text,
    blocks: [
      {
        type: "order_card",
        order: {
          id: order.id || null,
          receipt_number: order.receipt_number || "",
          date: order.date || order.created_at || "",
          status: order.status || "processing",
          total: Number(order.total || 0),
          items: Array.isArray(order.items) ? order.items : [],
          tracking: order.tracking || "",
        },
      },
    ],
    meta: {
      kind: "order_confirmation",
      orderId,
    },
  };

  const alreadyExists = messages.some(
    (msg) =>
      msg?.meta?.kind === "order_confirmation" &&
      String(msg?.meta?.orderId) === orderId
  );

  if (alreadyExists) return;

  setMessages((prev) => [...prev, message]);
  setScreen("chat");

  try {
    await saveChatMessage(forcedChatId || chatId, message);
  } catch (e) {
    console.error("save order confirmation error:", e);
  }
}

  async function onCtaClick(cta) {
    if (cta?.disabled) return;
    const action = cta?.action || {};
    await onAction(action.type ? action : { type: "send", message: cta.label });
  }

  async function onAction(action) {
    return dispatchSessionAction({
      action,
      send,
      setScreen,
      cart,
      setCart,
      setMessages,
      productIndexRef,
    });
  }

  async function loadChat(externalId) {
    await loadChatSession({
      externalId,
      setLoading,
      setChatId,
      setMessages,
      setCtas,
      setScreen,
      productIndexRef,
    });
  }

  function startNewChat() {
    setChatId(newId());
    setMessages(getInitialMessages());
    setCtas(getInitialCTAs());
    setScreen("chat");
  }

  const resetSession = useCallback(() => {
    setChatId(newId());
    setMessages(getInitialMessages());
    setCtas(getInitialCTAs());
    setScreen("chat");
    setCart({});
  }, []);

  return {
    chatId,
    setChatId,
    loadChat,
    startNewChat,
    resetSession,

    screen,
    setScreen,

    messagesWithTyping,
    ctas,
    loading,

    cart,
    cartItems,
    cartSummary,

    preset,
    setPreset,

    send,
    onCtaClick,
    onAction,
    appendOrderConfirmation,
  };
}